import React, { useEffect } from 'react';
import { Box, IconButton, Snackbar } from '@mui/material';
import Fade from '@mui/material/Fade';
import { X } from 'lucide-react';
import type { Notification } from '../../../../types/notification';
import { NotificationType } from '../../../../types/notification';
import { getNotificationTitle, getNotificationMessage } from '../../../utils/notificationMessages';

interface OfferNotificationToastProps {
  notification: Notification;
  onClose: () => void;
  index: number;
  isTraveler?: boolean;
}

const OfferNotificationToast: React.FC<OfferNotificationToastProps> = ({
  notification,
  onClose,
  index,
  isTraveler,
}) => {
  const role = isTraveler ? 'traveller' : 'sender';

  const DELIVERY_STATUS_TYPES = [
    'sender_drop_off_confirmed',
    'receiver_checkout_confirmed',
    'traveler_pickup_confirmed',
    'traveler_delivery_checkout_confirmed',
    'dropoff_reminder',
    'pickup_reminder',
    NotificationType.SHIPMENT_OFFER_WITHDRAWN,
    NotificationType.OFFER_EXPIRY_REMINDER,
    NotificationType.OFFER_EXPIRED,
  ];

  useEffect(() => {
    let duration: number | null = null;
    if (
      notification.type === NotificationType.CHAT_MESSAGE ||
      notification.type === NotificationType.CHAT_ROOM_CREATED ||
      notification.type === NotificationType.ORDER_GROUP_CHAT
    ) {
      duration = 5000;
    } else if (notification.type === NotificationType.SHIPMENT_OFFER_REQUEST) {
      duration = 10000;
    } else if (DELIVERY_STATUS_TYPES.includes(notification.type as string)) {
      duration = 15000;
    }
    if (duration === null) return;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [notification.type, onClose]);

  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Fade}
      sx={{
        top: { xs: `${80 + index * 120}px`, sm: `${100 + index * 120}px` },
        right: { xs: '16px', sm: '24px' },
        zIndex: 10000,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E9EAEB',
          borderRadius: '12px',
          padding: { xs: '16px', sm: '20px' },
          minWidth: { xs: 'min(320px, 90vw)', sm: '420px' },
          maxWidth: { xs: '90vw', sm: '460px' },
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '28px',
            height: '28px',
            padding: '4px',
            color: '#A4A7AE',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          aria-label="Close notification"
        >
          <X size={20} />
        </IconButton>

        {/* Title */}
        <Box
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: { xs: '16px', sm: '18px' },
            lineHeight: '24px',
            color: '#181D27',
            paddingRight: '32px',
          }}
        >
          {getNotificationTitle(notification, role)}
        </Box>

        {/* Message */}
        {notification.type !== NotificationType.CHAT_MESSAGE && notification.type !== NotificationType.ORDER_GROUP_CHAT && (
          <Box
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: '21px',
              color: '#535862',
              whiteSpace: 'pre-wrap',
              paddingRight: '8px',
            }}
            dangerouslySetInnerHTML={{
              __html: getNotificationMessage(notification, role)
                .replace(/\*\*("?[^*]+"?)\*\*/g, '<strong style="font-weight:600;color:#181D27">$1</strong>')
                .replace(/\n/g, '<br/>'),
            }}
          />
        )}

      </Box>
    </Snackbar>
  );
};

export default OfferNotificationToast;
